import React, { useContext, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AppContext } from "../Auth/contexts/AppContext";

dayjs.extend(utc);
dayjs.extend(timezone);

const SimpleSalesChart = ({ itemdata, grade }) => {
	const appContext = useContext(AppContext);
	const { userTZ } = appContext;

	// --- START: MODIFIED SCALING LOGIC ---

	// 1. Calculate the single highest quantity from ALL grades.
	const overallMaxQuantity = useMemo(() => {
		const allQuantities = [
			...(itemdata.soldgradea || []).map(item => item.Quantity),
			...(itemdata.soldgradeb || []).map(item => item.Quantity),
			...(itemdata.soldgradec || []).map(item => item.Quantity)
		];
		// Return 0 if empty, otherwise the max.
		return Math.max(0, ...allQuantities);
	}, [itemdata]);
    
    // 2. Define the ceiling for the Y-axis scale.
    //    It's either the actual max quantity or 10, whichever is larger.
    const scaleMax = Math.max(overallMaxQuantity, 10);

	// 3. Define a fixed pixel height for the chart canvas.
	const chartHeight = 60;

	// --- END: MODIFIED SCALING LOGIC ---

	const orderItems = useMemo(() => {
		if (grade === "A") return itemdata.soldgradea;
		if (grade === "B") return itemdata.soldgradeb;
		if (grade === "C") return itemdata.soldgradec;
		return [];
	}, [itemdata, grade]);

	const chartData = useMemo(() => {
		const startDate = dayjs().tz(userTZ).subtract(90, 'day').startOf('day');
		const salesByDay = {};
		(orderItems || []).forEach(item => {
			const itemDate = dayjs.utc(item.order.OrderDate).tz(userTZ);
			if (itemDate.isAfter(startDate)) {
				const dateKey = itemDate.format('YYYY-MM-DD');
				salesByDay[dateKey] = (salesByDay[dateKey] || 0) + item.Quantity;
			}
		});
		const series = [];
		for (let i = 89; i >= 0; i--) {
			const date = dayjs().tz(userTZ).subtract(i, 'day').startOf('day');
			const dateKey = date.format('YYYY-MM-DD');
			series.push({ date: dateKey, quantity: salesByDay[dateKey] || 0 });
		}
		return series;
	}, [orderItems, userTZ]);

	const trendLine = useMemo(() => {
		if (chartData.length < 2) return null;
		const offset = 1;
		const n = chartData.length;
		let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
		chartData.forEach((point, index) => {
			sumX += index;
			sumY += point.quantity;
			sumXY += index * point.quantity;
			sumX2 += index * index;
		});
		let slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) || 0;
		const intercept = (sumY - slope * sumX) / n;
		const exaggerationFactor = 4; //Try other values here if you want to.
		slope = slope * exaggerationFactor;
		slope = Math.min(slope, 1);
		slope = Math.max(slope, -0.5);
		const startY = intercept + offset;
		const endY = slope * (n - 1) + intercept + offset;
		return { startY: Math.max(0, startY), endY: Math.max(0, endY), slope };
	}, [chartData]);
    
    const chartbackground = (slope) => {
		const clampedSlope = Math.max(-3, Math.min(3, (slope * 100).toFixed(2)));
		let r, g, b;
		if (clampedSlope <= -1.5) { const t = (clampedSlope + 3) / 1.5; r = 255; g = Math.round(165 * t); b = 0;
		} else if (clampedSlope < 0) { const t = (clampedSlope + 1.5) / 1.5; r = 255; g = Math.round(165 + (90 * t)); b = 0;
		} else if (clampedSlope <= 1.5) { const t = clampedSlope / 1.5; r = Math.round(255 * (1 - t)); g = 255; b = 0;
		} else { const t = (clampedSlope - 1.5) / 1.5; r = 0; g = Math.round(255 - (155 * t)); b = Math.round(255 * t); }
		return `rgb(${r}, ${g}, ${b})`;
	}

	const SalesTrend = ({ trendline }) => {
		if (!trendline) return null;
		const slope = (trendline.slope * 100);

		let chartnotice = "Steady";
		if (slope < -2) chartnotice = "Quickly Decreasing";
		else if (slope < -1) chartnotice = "Decreasing";
		else if (slope < -0.5) chartnotice = "Slowly Decreasing";
		else if (slope < -0.25) chartnotice = "Steady";
		else if (slope < 0) chartnotice = "Steady";
		else if (slope < 0.25) chartnotice = "Steady";
		else if (slope < 0.50) chartnotice = "Increasing Slowly";
		else if (slope < 1) chartnotice = "Increasing";
		else if (slope > 1) chartnotice = "Quickly Increasing";
		
		return (
			<div style={{position:"absolute", top:"5px", left:"5px", padding:"2px 4px", border:"1px solid #AAA", backgroundColor: chartbackground(trendline?.slope || 0), borderRadius:"3px"}}>
				<div style={{ fontSize: "10px"}} title={slope.toFixed(3)}>{chartnotice}</div>
			</div>
		);
	};

	return (
        // Add position: relative to allow the absolute positioning of SalesTrend
		<div className="chart-container" style={{ position: 'relative' }}>
			<SalesTrend trendline={trendLine} /> 
			
			<svg width="270" height={chartHeight + 2}>
				{/* Bars */}
				{chartData.map((day, index) => {
					// Use `scaleMax` for all proportional calculations
					const barHeight = (day.quantity / scaleMax) * chartHeight;
					const x = index * 3;
					const y = chartHeight - barHeight;

					return (
						<rect
							key={day.date}
							x={x}
							y={y}
							width="3"
							height={barHeight}
							fill="#3b82f6"
							className="bar"
						>
							<title>{`${day.date}: ${day.quantity} items`}</title>
						</rect>
					);
				})}

				{/* Trend Line */}
				{trendLine && (
					<line
						x1="0"
                        // Also scale the trendline points against `scaleMax`
						y1={chartHeight - (trendLine.startY / scaleMax) * chartHeight}
						x2="267"
						y2={chartHeight - (trendLine.endY / scaleMax) * chartHeight}
						stroke="#b32525"
						strokeWidth="1"
						strokeDasharray="4 2"
						opacity="0.8"
					>
						<title>{`Trend slope: ${trendLine.slope.toFixed(3)}`}</title>
					</line>
				)}

				{/* Axes */}
				<line x1="0" y1={chartHeight} x2="270" y2={chartHeight} stroke="#DDD" strokeWidth="1" />
				<line x1="0" y1="0" x2="0" y2={chartHeight} stroke="#DDD" strokeWidth="1" />
			</svg><br />
		</div>
	);
}

export default SimpleSalesChart;